import React, { useEffect, useState, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import {
  getTraineeStats,
  getMyCourses,
  getCourseCompletePercentage,
  getAnnouncementsForUser,
  getMetadataCourseDetails,
  getOrganizationDocs,
  getOrganizationDocFileUrl,
  getProfile,
} from '../../../services/userService';
import { getSubtreeCourseSummary } from '../../../services/adminService';
import { encodeData } from '../../../utils/encodeAndEncode';
import moment from 'moment-timezone';

const MAX_IN_PROGRESS = 5;
const MY_COURSES_PAGE_LIMIT = 50; // fetch more courses so we don't miss in-progress ones

function UserDashboard() {
  const [traineeStats, setTraineeStats] = useState({
    certificate: 0,
    ranking: 0,
    points: 0,
    totalClassrooms: 0,
    totalCourses: 0,
    totalFreeCoursesPurchased: 0,
    totalPaidCoursesPurchased: 0,
    totalCompletedCourses: 0,
    totalHoursLearned: 0,
    mandatoryCompleted: 0,
    mandatoryPending: 0,
  });
  const [userName, setUserName] = useState('');
  const [inProgressList, setInProgressList] = useState([]);
  const [inProgressLoading, setInProgressLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);
  const [announcementExpandedId, setAnnouncementExpandedId] = useState(null);
  const [organizationDocs, setOrganizationDocs] = useState([]);
  const [organizationDocsLoading, setOrganizationDocsLoading] = useState(true);
  const [recommendedCourse, setRecommendedCourse] = useState(null);
  const [recentlyAssigned, setRecentlyAssigned] = useState([]);

  const [profileData, setProfileData] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamAllRows, setTeamAllRows] = useState([]);
  const [teamAttentionRows, setTeamAttentionRows] = useState([]);

  const navigate = useNavigate();

  const fetchTraineeStats = useCallback(async () => {
    try {
      const response = await getTraineeStats();
      if (response?.data) {
        setTraineeStats((prev) => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching trainee stats', error);
    }
  }, []);

  const fetchOrganizationDocs = useCallback(async () => {
    try {
      setOrganizationDocsLoading(true);
      const resp = await getOrganizationDocs({ page: 1, limit: 6 });
      const list = resp?.data?.docs || resp?.data?.data?.docs || resp?.docs || [];
      setOrganizationDocs(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error fetching organization docs', error);
      setOrganizationDocs([]);
    } finally {
      setOrganizationDocsLoading(false);
    }
  }, []);

  const fetchInProgressCourses = useCallback(async () => {
    try {
      setInProgressLoading(true);
      const response = await getMyCourses({ page: 1, limit: MY_COURSES_PAGE_LIMIT, search: '' });
      
      if (!response?.success || !response?.data?.courses?.length) {
        setInProgressList([]);
        return;
      }

      const courses = response.data.courses;
      const metadata = response.data.metadata || [];
      
      const completionPromises = courses.map(async (c, index) => {
        const courseId = c.course_id ?? c.id;
        if (!courseId) return { course: c, meta: metadata[index], completion: 100, trainerInfo: null };
        
        try {
          // Fetch completion percentage
          const completionRes = await getCourseCompletePercentage(courseId);
          
          // Fetch course details to get trainer info
          const detailsRes = await getMetadataCourseDetails({ course_id: courseId });
          
          return {
            course: c,
            meta: metadata[index],
            completion: completionRes?.data?.overallCompletionPercentage != null
              ? parseFloat(completionRes.data.overallCompletionPercentage)
              : 0,
            trainerInfo: detailsRes?.data?.trainer || null,
          };
        } catch (error) {
          console.error(`Error fetching data for course ${courseId}:`, error);
          return {
            course: c,
            meta: metadata[index],
            completion: 0,
            trainerInfo: null,
          };
        }
      });

      const results = await Promise.all(completionPromises);
      
      const inProgress = results
        .filter((r) => r.completion < 100)
        .sort((a, b) => a.completion - b.completion)
        .slice(0, MAX_IN_PROGRESS)
        .map((r) => {
          const id = r.course.course_id ?? r.course.id;
          const trainerName = r.trainerInfo?.name || r.course.trainer_name || r.course.instructor_name || '';
          return {
            id,
            title: r.course.course_name || r.course.title || 'Untitled Course',
            image: r.course.banner_image || r.course.image,
            trainer: trainerName,
            materialsCount: r.meta?.total_modules ?? 0,
            completion: Math.round(r.completion),
          };
        });

      // Find a pending mandatory course to recommend
      const mandatoryPendingResult = results.find((r) => Number(r.course.is_mandatory) === 1 && r.completion < 100);
      const recommendedId = mandatoryPendingResult
        ? (mandatoryPendingResult.course.course_id ?? mandatoryPendingResult.course.id)
        : null;

      if (mandatoryPendingResult) {
        const trName = mandatoryPendingResult.trainerInfo?.name || mandatoryPendingResult.course.trainer_name || mandatoryPendingResult.course.instructor_name || '';
        setRecommendedCourse({
          id: recommendedId,
          title: mandatoryPendingResult.course.course_name || mandatoryPendingResult.course.title || 'Untitled Course',
          image: mandatoryPendingResult.course.banner_image || mandatoryPendingResult.course.image,
          trainer: trName,
          materialsCount: mandatoryPendingResult.meta?.total_modules ?? 0,
          completion: Math.round(mandatoryPendingResult.completion),
          category: mandatoryPendingResult.course.category?.name || mandatoryPendingResult.course.category || '',
          level: mandatoryPendingResult.course.difficulty_level || mandatoryPendingResult.course.level || '',
          price: mandatoryPendingResult.course.price ?? null,
          isFree: mandatoryPendingResult.course.is_free ?? 0,
        });
      } else {
        setRecommendedCourse(null);
      }

      // Exclude the recommended course from in-progress list to avoid duplication
      const inProgressFiltered = inProgress.filter((item) => item.id !== recommendedId);

      // Identify recently assigned courses (enrolled in last 7 days, not yet started, NOT mandatory)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const seenCourseIds = new Set();
      const recentlyAssignedCourses = results
        .filter((r) => {
          const enrolledDate = new Date(r.course.updatedAt || r.course.createdAt || r.course.enrolled_at);
          const isMandatory = Number(r.course.is_mandatory) === 1;
          const courseId = r.course.course_id ?? r.course.id;
          // Deduplicate by course ID
          if (seenCourseIds.has(courseId)) return false;
          const passes = enrolledDate >= sevenDaysAgo && r.completion === 0 && !isMandatory && courseId !== recommendedId;
          if (passes) seenCourseIds.add(courseId);
          return passes;
        })
        .slice(0, 3)
        .map((r) => ({
          id: r.course.course_id ?? r.course.id,
          title: r.course.course_name || r.course.title || 'Untitled Course',
          image: r.course.banner_image || r.course.image,
        }));
      setRecentlyAssigned(recentlyAssignedCourses);

      // Also remove recently assigned (0% not-started) from "In Progress" to avoid duplication
      const recentlyAssignedIds = new Set(recentlyAssignedCourses.map(c => c.id));
      const finalInProgress = inProgressFiltered.filter(
        (item) => !recentlyAssignedIds.has(item.id) && item.completion > 0
      );

      setInProgressList(finalInProgress);
    } catch (error) {
      console.error('Error fetching in-progress courses', error);
      setInProgressList([]);
    } finally {
      setInProgressLoading(false);
    }
  }, []);

  const fetchAnnouncements = useCallback(async () => {
    try {
      setAnnouncementsLoading(true);
      const response = await getAnnouncementsForUser();
      if (!response?.success || !Array.isArray(response?.data)) {
        setAnnouncements([]);
        return;
      }
      const now = moment.utc();
      const published = (response.data || [])
        .filter((a) => a.status === 1 && a.scheduleDateTime && moment.utc(a.scheduleDateTime).isSameOrBefore(now))
        .sort((a, b) => new Date(b.scheduleDateTime) - new Date(a.scheduleDateTime))
        .slice(0, 3);
      setAnnouncements(published);
    } catch (error) {
      console.error('Error fetching announcements', error);
      setAnnouncements([]);
    } finally {
      setAnnouncementsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTraineeStats();
    fetchInProgressCourses();
    fetchAnnouncements();
    fetchOrganizationDocs();
  }, [fetchTraineeStats, fetchInProgressCourses, fetchAnnouncements, fetchOrganizationDocs]);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const prof = await getProfile();
        const profile = prof?.data;
        setProfileData(profile);

        if (!profile?.has_reportees) {
          setTeamAllRows([]);
          setTeamAttentionRows([]);
          return;
        }

        setTeamLoading(true);
        const res = await getSubtreeCourseSummary();
        const rows = Array.isArray(res?.data?.rows)
          ? res.data.rows
          : Array.isArray(res?.data)
            ? res.data
            : [];
        const normalized = Array.isArray(rows) ? rows : [];
        setTeamAllRows(normalized);

        const attention = normalized
          .slice()
          .filter((r) => Number(r?.enrolled_count || 0) > 0)
          .sort((a, b) => Number(a?.completion_percent ?? 101) - Number(b?.completion_percent ?? 101))
          .slice(0, 5);
        setTeamAttentionRows(attention);
      } catch (e) {
        setTeamAllRows([]);
        setTeamAttentionRows([]);
      } finally {
        setTeamLoading(false);
      }
    };

    fetchTeam();
  }, []);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        const name = parsed?.name || parsed?.full_name || parsed?.username;
        if (name) setUserName(name.split(' ')[0]);
      }
    } catch (err) {
      console.error('Error parsing user from localStorage', err);
    }
  }, []);

  const handleContinueCourse = (courseId) => {
    const encoded = encodeData({ id: courseId });
    navigate(`/user/courses/WatchCourse/${encodeURIComponent(encoded)}`);
  };

  const certCount = traineeStats.certificate || 0;
  const completedCount = traineeStats.totalCompletedCourses || 0;
  const totalEnrolled = traineeStats.totalCourses || 0;
  const mandatoryPending = traineeStats.mandatoryPending || 0;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  // Helper for generic fallback image
  const defaultAction = () => {
    navigate('/user/courses');
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left Column (Main Content) */}
      <div className="flex-1 space-y-6 lg:max-w-calc(100%-320px) min-w-0">
        
        {/* Hero Banner */}
        <div className="bg-[#f8faff] rounded-2xl border border-blue-50/50 p-6 md:p-8 flex items-center justify-between shadow-sm relative overflow-hidden min-h-[250px]">
          <div className="z-10 max-w-md relative">
            <h1 className="text-[28px] leading-tight md:text-3xl font-bold text-gray-900 mb-2">{getGreeting()}{userName ? `, ${userName}` : ''}</h1>
            <p className="text-[14px] text-gray-500 mb-6 max-w-[320px] leading-snug">Ready to keep learning? Let's keep your journey going. You're just one step closer to your goals.</p>
            <div className="flex flex-wrap gap-3">
              <button 
                onClick={defaultAction}
                className="bg-[#7083FF] text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#5a6dff] transition shadow-sm"
              >
                Resume Last Course
              </button>
              <button 
                onClick={defaultAction}
                className="border border-[#7083FF] text-[#7083FF] bg-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-50 transition"
              >
                Explore New Course
              </button>
            </div>
          </div>
          <div className="hidden sm:flex absolute right-0 sm:-right-6 lg:right-4 bottom-0 pointer-events-none z-0 items-end pb-2">
             <img 
               src="/images/logos/user_illustraion.png" 
               alt="Learning Illustration" 
               className="h-[210px] md:h-[250px] w-auto object-contain opacity-95" 
             />
          </div>
        </div>

        {/* 4 Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
           {/* Card 1: Certificate */}
           <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm flex flex-col items-start justify-between min-h-[110px]">
             <div className="w-7 h-7 mb-2 text-gray-500">
               <Icon icon="fluent:certificate-24-regular" width={22} height={22} />
             </div>
             <div>
                <p className="text-[12px] text-gray-500 mb-1">Certificate</p>
                <p className="text-[22px] font-bold text-gray-900 leading-none">{certCount}</p>
             </div>
           </div>
           
           {/* Card 2: Courses Completed */}
           <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm flex flex-col items-start justify-between min-h-[110px]">
             <div className="w-7 h-7 mb-2 text-gray-500">
               <Icon icon="fluent:clipboard-task-24-regular" width={22} height={22} />
             </div>
             <div>
                <p className="text-[12px] text-gray-500 mb-1">Courses Completed</p>
                <p className="text-[22px] font-bold text-gray-900 leading-none">{completedCount}</p>
             </div>
           </div>
           
           {/* Card 3: Enrolled Courses */}
           <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm flex flex-col items-start justify-between min-h-[110px]">
             <div className="w-7 h-7 mb-2 text-gray-500">
               <Icon icon="fluent:book-open-24-regular" width={22} height={22} />
             </div>
             <div>
                <p className="text-[12px] text-gray-500 mb-1">Enrolled Courses</p>
                <p className="text-[22px] font-bold text-gray-900 leading-none">{totalEnrolled}</p>
             </div>
           </div>
           
           {/* Card 4: Mandatory course pending */}
           <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm flex flex-col items-start justify-between min-h-[110px]">
             <div className="w-7 h-7 mb-2 text-gray-500">
               <Icon icon="fluent:play-circle-24-regular" width={22} height={22} />
             </div>
             <div>
                <p className="text-[12px] text-gray-500 mb-1 leading-snug break-words hyphens-auto max-w-[90px]">Mandatory Pending</p>
                <p className="text-[22px] font-bold text-gray-900 leading-none">{mandatoryPending}</p>
             </div>
           </div>
        </div>

        {/* Recently Assigned Courses Highlight */}
        {recentlyAssigned.length > 0 && (
          <div className="mb-6">
            <div className="rounded-2xl border-2 border-[#7083FF]/20 bg-gradient-to-r from-[#7083FF]/5 to-indigo-50 p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-[#7083FF] flex items-center justify-center">
                  <Icon icon="fluent:sparkle-24-filled" width={18} height={18} className="text-white" />
                </div>
                <h3 className="text-[15px] font-semibold text-gray-900">New Courses Assigned to You</h3>
              </div>
              <div className="flex flex-wrap gap-3">
                {recentlyAssigned.map((course) => (
                  <button
                    key={course.id}
                    type="button"
                    onClick={() => handleContinueCourse(course.id)}
                    className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-gray-100 shadow-sm hover:shadow-md hover:border-[#7083FF]/30 transition-all"
                  >
                    <img src={course.image || 'https://placehold.co/40x40/e2e8f0/64748b?text=C'} alt="" className="w-10 h-10 rounded-lg object-cover" onError={(e) => { e.target.src = 'https://placehold.co/40x40/e2e8f0/64748b?text=C'; }} />
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900 line-clamp-1">{course.title}</p>
                      <p className="text-xs text-[#7083FF] font-medium">Start now →</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* In Progress learning content */}
        <div>
          <h2 className="text-[18px] font-semibold text-gray-800 mb-4">In Progress learning content</h2>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden p-2">
            {inProgressLoading ? (
              <div className="flex items-center justify-center py-12">
                <span className="w-8 h-8 border-2 border-[#7083FF] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : inProgressList.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 text-sm">No courses in progress.</p>
              </div>
            ) : (
              <div className="max-h-[350px] overflow-y-auto custom-scrollbar pr-2">
                <ul className="flex flex-col divide-y divide-gray-100">
                {inProgressList.map((item) => (
                  <li
                    key={item.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleContinueCourse(item.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleContinueCourse(item.id);
                      }
                    }}
                    className="flex flex-row items-center gap-4 py-3 px-3 rounded-xl hover:bg-gray-50 transition border border-transparent cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <div className="w-20 h-16 rounded-lg bg-gray-200 shrink-0 overflow-hidden">
                       <img src={item.image || 'https://placehold.co/120x80/e2e8f0/64748b?text=Course'} alt="thumbnail" className="w-full h-full object-cover" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[14px] font-medium text-gray-900 line-clamp-2 leading-tight mb-1">
                        {item.title}
                      </h3>
                      <p className="text-[13px] text-gray-500 truncate">{item.trainer}</p>
                    </div>

                    <div className="flex flex-nowrap items-center gap-4 shrink-0 ml-auto justify-between">
                       <div className="flex flex-col text-[12px]">
                          <span className="text-gray-400 mb-0.5">Content</span>
                          <span className="font-medium text-gray-700 flex items-center gap-1.5">
                             <Icon icon="fluent:document-text-24-regular" width={14} className="text-gray-400"/> 
                             {item.materialsCount} Materials
                          </span>
                       </div>
                       
                       <div className="flex w-[120px] items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                            style={{ background: `conic-gradient(#7083FF ${item.completion || 0}%, #eff6ff 0)` }}
                          >
                             <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-[10px] font-bold text-[#7083FF]">
                                {item.completion || 0}%
                             </div>
                          </div>
                          <div className="flex flex-col">
                             <span className="text-[12px] text-gray-400 font-medium uppercase tracking-wider">Progress</span>
                          </div>
                       </div>
                       
                       <button
                         type="button"
                         onClick={(e) => {
                           e.stopPropagation();
                           handleContinueCourse(item.id);
                         }}
                         className={
                           item.completion === 0
                             ? 'px-4 py-1.5 rounded-lg text-[13px] font-semibold bg-[#7083FF] text-white hover:bg-[#5a6dff] transition whitespace-nowrap ml-auto'
                             : 'px-4 py-1.5 rounded-lg text-[13px] font-medium text-gray-700 border border-gray-200 hover:bg-gray-100 transition whitespace-nowrap ml-auto'
                         }
                       >
                         {item.completion === 0 ? 'Begin Module' : 'Continue'}
                       </button>
                    </div>
                  </li>
                ))}
                </ul>
              </div>
            )}
            {/* Divider lines are handled by mapping implicitly, but wait, the design shows faint lines between items. Let's add border to ul or items. */}
          </div>
        </div>

        {/* Recommended For You */}
        <div>
          <h2 className="text-[18px] font-semibold text-gray-800 mb-4">Recommended For You</h2>
          {inProgressLoading ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center text-sm text-gray-500">Loading recommendations...</div>
          ) : recommendedCourse ? (
             <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
               <div className="flex gap-4 items-center w-full">
                 <div className="w-32 h-24 bg-gray-100 rounded-lg shrink-0 relative flex flex-col p-0 overflow-hidden">
                    <img src={recommendedCourse.image || 'https://placehold.co/120x80/e2e8f0/64748b?text=Course'} alt="thumbnail" className="absolute inset-0 w-full h-full object-cover z-0" />
                    <div className="z-10 bg-white/90 backdrop-blur-sm self-start text-[10px] font-medium px-2 py-0.5 rounded text-gray-800 m-2 mt-auto border border-white/50 shadow-sm">
                      {recommendedCourse.materialsCount} Modules
                    </div>
                 </div>
                 <div className="flex-1 min-w-0 py-1">
                   {/* Mandatory Badge */}
                   <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-600 text-[10px] font-bold px-2 py-0.5 rounded border border-amber-100 uppercase tracking-wide mb-1.5">
                      <Icon icon="fluent:warning-20-filled" width={10} />
                      Mandatory
                   </span>
                   <h3 className="text-[15px] font-medium text-gray-900 leading-tight mb-1 line-clamp-2">{recommendedCourse.title}</h3>
                   <p className="text-[13px] text-gray-500 mb-2 truncate">{recommendedCourse.trainer}</p>
                   <div className="flex items-center gap-2 flex-wrap">
                     {recommendedCourse.category && (
                       <span className="text-[11px] font-medium border border-gray-200 rounded px-2 py-0.5 text-gray-600 truncate max-w-[120px]">
                         {recommendedCourse.category}
                       </span>
                     )}
                     {recommendedCourse.level && (
                       <span className="text-[11px] font-medium border border-gray-200 rounded px-2 py-0.5 text-gray-600">
                         {recommendedCourse.level}
                       </span>
                     )}
                   </div>
                 </div>
               </div>
               
               <div className="flex flex-row md:flex-col items-center md:items-end justify-between md:justify-center shrink-0 w-full md:w-auto mt-2 md:mt-0">
                 {/* <p className="text-[18px] font-bold text-gray-900 md:mb-4">
                   {Number(recommendedCourse.isFree) === 1 || recommendedCourse.price === 0 ? 'Free' : `₹${recommendedCourse.price || 0}`}
                 </p> */}
                 <button 
                   onClick={() => handleContinueCourse(recommendedCourse.id)}
                   className="px-5 py-2 md:py-1.5 rounded-lg text-[13px] font-semibold text-white bg-[#7083FF] hover:bg-[#5b6eef] transition shadow-sm w-full md:w-auto"
                 >
                   {recommendedCourse.completion === 0 ? 'Start Course' : 'Resume'}
                 </button>
               </div>
             </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 flex flex-col items-center justify-center text-center">
              <Icon icon="fluent:sparkle-24-regular" width={32} className="text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">You're all caught up on your mandatory courses!</p>
            </div>
          )}
        </div>

        {Boolean(profileData?.has_reportees) && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[18px] font-semibold text-gray-800">Team Course Progress</h2>
              <button
                className="text-[12px] font-medium text-[#7083FF] hover:text-[#5b6eef] transition"
                onClick={() => navigate('/user/team-course-progress')}
              >
                View All
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <p className="text-xs text-gray-400">Quick view of your reportees. Click “View All” for full details.</p>
              </div>

              {teamLoading ? (
                <div className="flex items-center justify-center py-12">
                  <span className="w-8 h-8 border-2 border-[#7083FF] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : teamAllRows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center px-6">
                  <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center mb-3">
                    <Icon icon="fluent:people-team-20-regular" width={22} className="text-gray-300" />
                  </div>
                  <p className="text-sm font-medium text-gray-400">No team progress data</p>
                  <p className="text-xs text-gray-300 mt-0.5">Assign courses to your reportees to see progress.</p>
                </div>
              ) : (
                (() => {
                  const totals = (teamAllRows || []).reduce(
                    (acc, r) => {
                      acc.enrolled += Number(r?.enrolled_count || 0);
                      acc.completed += Number(r?.completed_count || 0);
                      acc.inProgress += Number(r?.in_progress_count || 0);
                      acc.notStarted += Number(r?.not_started_count || 0);
                      return acc;
                    },
                    { enrolled: 0, completed: 0, inProgress: 0, notStarted: 0 }
                  );
                  const denom = totals.completed + totals.inProgress + totals.notStarted;
                  const completedPct = denom ? (totals.completed / denom) * 100 : 0;
                  const inProgressPct = denom ? (totals.inProgress / denom) * 100 : 0;
                  const notStartedPct = denom ? (totals.notStarted / denom) * 100 : 0;

                  return (
                    <div className="p-5 space-y-5">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs font-semibold text-gray-700">Team Breakdown</div>
                          <div className="text-[11px] text-gray-400">{totals.enrolled} enrolled</div>
                        </div>
                        <div className="w-full h-3 rounded-full bg-gray-100 overflow-hidden flex">
                          <div className="h-full bg-emerald-500" style={{ width: `${completedPct}%` }} />
                          <div className="h-full bg-amber-400" style={{ width: `${inProgressPct}%` }} />
                          <div className="h-full bg-slate-300" style={{ width: `${notStartedPct}%` }} />
                        </div>
                        <div className="mt-3 flex flex-wrap gap-3 text-[11px]">
                          <div className="flex items-center gap-2 text-gray-600">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            <span className="font-semibold">Completed</span>
                            <span className="text-gray-400">{totals.completed}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <span className="w-2 h-2 rounded-full bg-amber-400" />
                            <span className="font-semibold">In Progress</span>
                            <span className="text-gray-400">{totals.inProgress}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <span className="w-2 h-2 rounded-full bg-slate-300" />
                            <span className="font-semibold">Not Started</span>
                            <span className="text-gray-400">{totals.notStarted}</span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs font-semibold text-gray-700">Needs Attention</div>
                          <div className="text-[11px] text-gray-400">Lowest completion</div>
                        </div>
                        <div className="divide-y divide-gray-100 rounded-2xl border border-gray-100 overflow-hidden">
                          {(teamAttentionRows || []).map((r, idx) => {
                            const completion = r?.completion_percent == null ? null : Number(r.completion_percent);
                            return (
                              <div
                                key={r?.user_id ?? idx}
                                className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                                onClick={() => navigate('/user/team-course-progress')}
                                role="presentation"
                              >
                                <div className="min-w-0">
                                  <div className="text-xs font-semibold text-gray-900 truncate max-w-[260px]">{r?.user_name || 'User'}</div>
                                  <div className="text-[11px] text-gray-400 truncate max-w-[260px]">{r?.user_email || ''}</div>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                  <div className="text-[11px] text-gray-500">{Number(r?.completed_count || 0)}/{Number(r?.enrolled_count || 0)}</div>
                                  <div className={`text-xs font-bold ${completion != null && completion < 40 ? 'text-rose-600' : 'text-gray-700'}`}>
                                    {completion == null ? '—' : `${completion}%`}
                                  </div>
                                  <Icon icon="lucide:chevron-right" width="14" className="text-gray-300" />
                                </div>
                              </div>
                            );
                          })}
                          {(!teamAttentionRows || teamAttentionRows.length === 0) && (
                            <div className="px-4 py-6 text-center text-sm text-gray-400">No reportees with enrolled courses</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()
              )}
            </div>
          </div>
        )}

      </div>

      {/* Right Column (Sidebar) */}
      <div className="w-full lg:w-[320px] xl:w-[340px] shrink-0 space-y-8">
        
        {/* Organization Documents */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[18px] font-semibold text-gray-800">Organization Documents</h2>
            <button className="text-[12px] font-medium text-[#7083FF] hover:text-[#5b6eef] transition" onClick={() => navigate('/user/organization-docs')}>View All</button>
          </div>
          
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 py-2">
            <div className="max-h-[280px] overflow-y-auto custom-scrollbar">
              {organizationDocsLoading ? (
                <div className="py-6 text-center text-sm text-gray-500">Loading...</div>
              ) : organizationDocs.length === 0 ? (
                <div className="py-6 text-center text-sm text-gray-500">No documents.</div>
              ) : (
                <ul className="divide-y divide-gray-100 px-2">
                  {organizationDocs.map((d) => (
                    <li key={d.id} className="py-2.5">
                      <button
                        type="button"
                        onClick={() => window.open(getOrganizationDocFileUrl(d.id), '_blank')}
                        className="w-full text-left flex items-center gap-3 px-2 py-1.5 hover:bg-gray-50 rounded-xl transition"
                      >
                        <div className="w-10 h-10 border border-gray-100 rounded-xl flex items-center justify-center shrink-0 text-gray-500 bg-gray-50">
                          <Icon icon="fluent:document-24-regular" width={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-semibold text-gray-800 truncate">{d.title || 'Untitled document'}</p>
                          <p className="text-[11px] text-gray-400 mt-0.5">Updated {d.createdAt ? moment.utc(d.createdAt).local().format('MMM DD, YYYY') : '—'}</p>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Organization Announcement */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[18px] font-semibold text-gray-800">Organization Announcement</h2>
            <button className="text-[12px] font-medium text-[#7083FF] hover:text-[#5b6eef] transition" onClick={() => navigate('/user/dashboard')}>View All</button>
          </div>
          
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 py-4 px-2 sm:px-4">
            <div className="max-h-[350px] overflow-y-auto custom-scrollbar pr-1 relative">
              
              {announcementsLoading ? (
                 <div className="py-6 text-center text-sm text-gray-500">Loading...</div>
              ) : announcements.length === 0 ? (
                 <div className="py-6 text-center text-sm text-gray-500">No announcements.</div>
              ) : (
                <div className="relative pl-1 md:pl-2">
                  {/* Timeline line */}
                  <div className="absolute left-[11px] md:left-[15px] top-4 bottom-6 w-[2px] bg-gray-100 z-0"></div>
                  
                  <div className="space-y-0 relative z-10">
                    {announcements.map((a, idx) => {
                      // highlight first announcement icon in blue, rest in gray
                      const isLatest = idx === 0;
                      const isExpanded = announcementExpandedId === a.id;
                      return (
                      <div key={a.id} className="flex gap-3 sm:gap-4 relative group">
                        <div className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center mt-1 z-10 ring-4 ring-white shadow-[0_0_0_2px_rgba(255,255,255,1)] ${isLatest ? 'bg-blue-50 text-[#7083FF]' : 'bg-gray-50 text-gray-400'}`}>
                          <Icon icon={isLatest ? "fluent:megaphone-16-filled" : "fluent:megaphone-16-regular"} width={14} />
                        </div>
                        <div 
                          className="flex-1 pb-5 border-b border-gray-100 last:border-0 last:pb-0 px-2 sm:px-3 py-2 -mt-1 -ml-1 sm:-ml-2 rounded-xl hover:bg-gray-50/80 transition-colors cursor-pointer" 
                          onClick={() => setAnnouncementExpandedId(isExpanded ? null : a.id)}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-[10px] font-medium text-gray-400 tracking-wider uppercase">{a.scheduleDateTime ? moment.utc(a.scheduleDateTime).local().format('MMM DD, YYYY') : '—'}</p>
                            <Icon icon={isExpanded ? "fluent:chevron-up-16-regular" : "fluent:chevron-down-16-regular"} className={`text-gray-400 transition-transform ${isExpanded ? '' : 'group-hover:translate-y-0.5'}`} />
                          </div>
                          <h3 className={`text-[14px] font-semibold text-gray-900 mb-1.5 pr-2 ${!isExpanded ? 'line-clamp-1' : ''}`}>{a.title}</h3>
                          <div className={`text-[13px] text-gray-600 leading-relaxed pr-2 ${isExpanded ? '' : 'line-clamp-2'}`}>
                            {a.message}
                          </div>
                        </div>
                      </div>
                    )})}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserDashboard;
